import { useState, useRef, useEffect } from 'react';
import { Check, X, Pencil } from 'lucide-react';

/**
 * InlineEdit - click-to-edit field component
 * @param {string} value - current value
 * @param {function} onSave - callback(newValue) called on save
 * @param {string} type - 'text' | 'number' | 'date' | 'select' | 'textarea'
 * @param {array} options - for select type [{value, label}]
 * @param {string} displayValue - custom display string (e.g. formatted INR)
 * @param {string} className - additional classes for the display text
 * @param {string} placeholder - placeholder when empty
 * @param {boolean} disabled - disable editing
 */
export default function InlineEdit({ value, onSave, type = 'text', options = [], displayValue, className = '', placeholder = 'Click to edit', disabled = false, prefix = '', suffix = '', testId = '' }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef(null);

  useEffect(() => { setDraft(value); }, [value]);
  useEffect(() => { if (editing && inputRef.current) inputRef.current.focus(); }, [editing]);

  const save = () => {
    if (draft !== value) {
      onSave(type === 'number' ? Number(draft) : draft);
    }
    setEditing(false);
  };

  const cancel = () => { setDraft(value); setEditing(false); };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && type !== 'textarea') save();
    if (e.key === 'Escape') cancel();
  };

  if (disabled) {
    return <span className={`${className}`}>{displayValue || prefix}{displayValue ? '' : (value || placeholder)}{!displayValue && suffix}</span>;
  }

  if (editing) {
    if (type === 'select') {
      return (
        <div className="flex items-center gap-1 animate-fade-in" data-testid={testId ? `${testId}-editing` : undefined}>
          <select ref={inputRef} value={draft} onChange={e => setDraft(e.target.value)} onBlur={save} onKeyDown={handleKeyDown}
            className="h-7 px-2 text-sm bg-white border border-[#3A8C8C] rounded-lg outline-none shadow-sm">
            {options.map(opt => <option key={opt.value || opt} value={opt.value || opt}>{opt.label || opt}</option>)}
          </select>
        </div>
      );
    }

    if (type === 'textarea') {
      return (
        <div className="animate-fade-in" data-testid={testId ? `${testId}-editing` : undefined}>
          <textarea ref={inputRef} value={draft || ''} onChange={e => setDraft(e.target.value)} onKeyDown={handleKeyDown}
            className="w-full px-2 py-1.5 text-sm bg-white border border-[#3A8C8C] rounded-lg outline-none shadow-sm resize-none" rows={3} />
          <div className="flex gap-1 mt-1">
            <button onClick={save} className="p-1 rounded-md bg-[#3A8C8C] text-white hover:bg-[#2E7A7A]"><Check size={12} /></button>
            <button onClick={cancel} className="p-1 rounded-md bg-[#E8E6E1] text-[#6B7280] hover:bg-[#D4D0C8]"><X size={12} /></button>
          </div>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-1 animate-fade-in" data-testid={testId ? `${testId}-editing` : undefined}>
        {prefix && <span className="text-sm text-[#6B7280]">{prefix}</span>}
        <input ref={inputRef} type={type === 'number' ? 'number' : type === 'date' ? 'date' : 'text'}
          value={draft || ''} onChange={e => setDraft(e.target.value)} onBlur={save} onKeyDown={handleKeyDown}
          className="h-7 px-2 text-sm bg-white border border-[#3A8C8C] rounded-lg outline-none shadow-sm min-w-[60px] max-w-full" />
        {suffix && <span className="text-sm text-[#6B7280]">{suffix}</span>}
      </div>
    );
  }

  const display = displayValue || `${prefix}${value || ''}${suffix}`;
  return (
    <span onClick={() => setEditing(true)}
      className={`cursor-pointer inline-flex items-center gap-1 group transition-colors hover:text-[#3A8C8C] ${className}`}
      title="Click to edit"
      data-testid={testId || undefined}>
      <span className={`${!value && !displayValue ? 'text-[#D4D0C8] italic' : ''}`}>{display || placeholder}</span>
      <Pencil size={11} strokeWidth={1.5} className="opacity-0 group-hover:opacity-60 text-[#3A8C8C] transition-opacity shrink-0" />
    </span>
  );
}
